import { useStore } from '@nanostores/react';
import { useCallback } from 'react';
import { Applications } from '~/components/dialogs/settings/Applications';
import { Backups } from '~/components/dialogs/settings/Backups';
import { FeaturePreviews } from '~/components/dialogs/settings/FeaturePreviews';
import { General } from '~/components/dialogs/settings/General/General';
import { Hosting } from '~/components/dialogs/settings/Hosting';
import { KnowledgePersonal } from '~/components/dialogs/settings/KnowledgePersonal';
import { KnowledgeTeam } from '~/components/dialogs/settings/KnowledgeTeam';
import { Team } from '~/components/dialogs/settings/Team';
import { PackagePromptsSection } from '~/components/dialogs/settings/Team/PackagePromptsSection';
import { RegistriesSettingsSection } from '~/components/dialogs/settings/Team/RegistriesSettingsSection';
import { TokenUsage } from '~/components/dialogs/settings/TokenUsage';
import { Analytics } from '~/components/dialogs/settings/project/Analytics';
import { Authentication } from '~/components/dialogs/settings/project/Authentication';
import { Domains } from '~/components/dialogs/settings/project/Domains';
import { EdgeFunctions } from '~/components/dialogs/settings/project/EdgeFunctions';
import { FileStorage } from '~/components/dialogs/settings/project/FileStorage';
import { KnowledgeProject } from '~/components/dialogs/settings/project/KnowledgeProject';
import { Project } from '~/components/dialogs/settings/project/Project';
import { Secrets } from '~/components/dialogs/settings/project/Secrets';
import { UserManagementView } from '~/components/dialogs/settings/project/UserManagement';
import { DatabaseSection } from '~/components/ui/shadcn/components/supabase-manager/DatabaseSection.client';
import { chatStore } from '~/lib/stores/chat';
import { SettingsPage } from '~/lib/stores/menu';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';

interface SettingsDialogContentProps {
  currentPage: SettingsPage;
  closeDialog: () => void;
  screen?: string;
}

export const SettingsDialogContent = ({ currentPage, closeDialog, screen }: SettingsDialogContentProps) => {
  const tier = useStore(userStore.getTierStore());
  const hasSubscription = tier && tier?.plan !== 'personal';
  const chatStarted = useStore(chatStore.started);
  const canUseExperimentalFeatures = hasSubscription ?? false;
  const activeOrganization = useStore(userStore.getActiveOrganization());

  const handleRegistriesChange = useCallback(
    (newRegistries: any[]) => {
      if (!activeOrganization) {
        return;
      }

      userStore.setActiveOrganization(
        {
          ...activeOrganization,
          customRegistries: newRegistries,
        },
        false,
      );
    },
    [activeOrganization],
  );

  return (
    <div
      className={classNames('flex-1 pb-10 relative overflow-y-auto flex flex-col bg-bolt-ds-surfaceOne', {
        'px-8': currentPage !== SettingsPage.DatabaseSection,
      })}
    >
      {/* Personal settings */}
      {currentPage === SettingsPage.General && <General />}
      {currentPage === SettingsPage.SubscriptionAndTokens && tier && (
        <TokenUsage tier={tier} closeDialog={closeDialog} activeOrganization={activeOrganization} />
      )}
      {currentPage === SettingsPage.Applications && <Applications />}
      {currentPage === SettingsPage.Cloud && <Hosting />}
      {currentPage === SettingsPage.KnowledgePersonal && <KnowledgePersonal />}
      {currentPage === SettingsPage.FeaturePreviews && <FeaturePreviews canAccess={canUseExperimentalFeatures} />}

      {/* Team settings */}
      {currentPage === SettingsPage.Team && <Team closeDialog={closeDialog} />}
      {currentPage === SettingsPage.KnowledgeTeam && <KnowledgeTeam />}
      {currentPage === SettingsPage.PrivateRegistries && activeOrganization && (
        <RegistriesSettingsSection organization={activeOrganization} onRegistriesChange={handleRegistriesChange} />
      )}
      {currentPage === SettingsPage.PackagesKnowledge && activeOrganization && (
        <PackagePromptsSection organization={activeOrganization} />
      )}

      {/* Project settings */}
      {chatStarted && currentPage === SettingsPage.Project && <Project />}
      {currentPage === SettingsPage.Backups && <Backups />}
      {currentPage === SettingsPage.KnowledgeProject && <KnowledgeProject />}
      {currentPage === SettingsPage.Analytics && <Analytics />}
      {currentPage === SettingsPage.Authentication && <Authentication />}
      {currentPage === SettingsPage.UserManagement && <UserManagementView />}
      {currentPage === SettingsPage.FileStorage && <FileStorage />}
      {currentPage === SettingsPage.Domains && <Domains step={screen} />}
      {currentPage === SettingsPage.Secrets && <Secrets />}
      {currentPage === SettingsPage.DatabaseSection && <DatabaseSection />}
      {currentPage === SettingsPage.EdgeFunctions && <EdgeFunctions />}
    </div>
  );
};
